import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import * as authApi from '../api';
import type { Permissions, Settings, User } from '../types';

const emptyPermissions: Permissions = {
  isStaff: false,
  isSuperadmin: false,
  canPublishNotice: false,
  canEditSettings: false,
  canManageRedeemCodes: false,
  canViewAudit: false,
  canExportBackup: false
};

const emptySettings: Settings = {
  siteName: '服务器委托中心',
  currency: '金币',
  initBalanceCents: 100000,
  registrationEnabled: true,
  registrationInviteRequired: true,
  registrationNotice: '',
  commissionPolicy: '',
  marketPolicy: '',
  auctionPolicy: '',
  tradeFeePercent: 5,
  allowUnboundBoardMessages: false,
  allowUnboundBoardComments: false,
  allowUnboundBoardLikes: false,
  feedbackEnabled: false
};

interface AuthContextValue {
  user: User | null;
  settings: Settings;
  permissions: Permissions;
  loading: boolean;
  login: (username: string, password: string) => Promise<void>;
  register: (username: string, password: string, inviteCode?: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [settings, setSettings] = useState<Settings>(emptySettings);
  const [permissions, setPermissions] = useState<Permissions>(emptyPermissions);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const session = await authApi.getSession();
      setUser(session.user);
      setSettings(session.settings);
      setPermissions(session.permissions);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    await authApi.login(username, password);
    await refresh();
  }, [refresh]);

  const register = useCallback(async (username: string, password: string, inviteCode?: string, displayName?: string) => {
    await authApi.register(username, password, inviteCode, displayName);
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    await authApi.logout();
    await refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, settings, permissions, loading, login, register, logout, refresh }),
    [user, settings, permissions, loading, login, register, logout, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}


